import { getBackendUrl } from "@Utils";
import { useTranslation } from "@i18n";
import type { TranslatedString } from "@i18n";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import IconComponent from "@CubeComponents/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, MouseEvent } from "react";
import { fileToBase64 } from "@Services/utils/helpers";
import { useDispatch } from "react-redux";
import { showToastNotification } from "@Reducers/toastSlice";
import { formatBytes, TOAST_TYPE_WARNING } from "@Services/constants";
import { convertDate } from "@Services/luxonHelper";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { getFileIcon } from "@CubeComponents/constants/globalFunctions";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { v4 as uuid } from "uuid";
import type { Attachment } from "./RateAnalysis";

type AttachmentsProps = {
  closeAttachmentModal: () => void;
  viewMode?: boolean;
  attachments?: Attachment[];
  handleAttachFiles: (files: Attachment[]) => void;
};

type SlickGridRef = {
  slickGrid: {
    getSelectedRows: () => number[];
    getDataItem: (index: number) => Attachment;
  };
  refresh: () => void;
};

function Attachments({
  closeAttachmentModal,
  viewMode = false,
  attachments = [],
  handleAttachFiles,
}: AttachmentsProps) {
  const { t } = useTranslation();
  const [attachedFilesData, setAttachedFilesData] = useState<Attachment[]>([]);
  const [removeBtnDisabled, setRemoveBtnDisabled] = useState(true);

  const dispatch = useDispatch();
  const dataGrid = useRef<SlickGridRef | null>(null);

  const toolbarOptions = useMemo(
    () => [
      {
        id: "delete_attachment",
        prefixIcon: "delete",
        name: t("Remove"),
        align: "Left" as const,
        disabled: removeBtnDisabled,
      },
    ],
    [removeBtnDisabled],
  );

  const rateAnalysisAttachementsDefinition = useRef([
    {
      field: "download",
      id: "download",
      name: "",
      minWidth: 40,
      maxWidth: 40,
      formatter: downLoadTemplate,
      excludeFromExport: true,
    },
    {
      field: "name",
      id: "name",
      name: t("File Name"),
      filterType: "Text",
      formatter: fileNameTemplate,
    },
    {
      field: "size",
      id: "size",
      name: t("Size"),
      filterType: "Text",
      formatter: sizeTemplate,
      _excelValueGetter: (row: Attachment) => formatBytes(row.size),
    },
    {
      field: "modifiedBy.fullName",
      id: "modifiedBy.fullName",
      name: t("Modified By"),
      filterType: "Text",
      formatter: (data, value, a, b, params) => params.modifiedBy?.fullName,
    },
    {
      field: "modifiedDate",
      id: "modifiedDate",
      name: t("Modified Date"),
      filterType: "Date",
      formatter: dateTemplate,
      _excelValueGetter: (row: Attachment) => convertDate(Number(row.modifiedDate), "dateForText"),
    },
  ]);

  useEffect(() => {
    if (attachments.length) {
      setAttachedFilesData(
        attachments.map((file: Attachment) => {
          return {
            ...file,
            id: uuid(),
          };
        }),
      );
    }
  }, [attachments]);

  const showToast = (content: TranslatedString, type: string) => {
    dispatch(showToastNotification({ content, type }));
  };

  const handleChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files;
    if (!file || !file.length) {
      return;
    }
    const [firstFile] = file;
    if (!firstFile) {
      return;
    }
    const fileType = firstFile.type;
    const name = firstFile.name;
    const isDataExist = attachedFilesData.find((file: Attachment) => file.name === name);
    if (isDataExist) {
      return showToast(t("Selected file already exists!"), TOAST_TYPE_WARNING);
    }
    const size = firstFile.size;
    const fileBase64Str = await fileToBase64(firstFile);
    const fileBase64 = typeof fileBase64Str === "string" ? fileBase64Str : "";
    setAttachedFilesData((prev) => [
      ...prev,
      {
        fileBase64Str: fileBase64,
        type: fileType,
        name,
        size,
        id: uuid(),
      },
    ]);
  };

  const handleDownloadClick = (params: Attachment) => (e: MouseEvent) => {
    e.stopPropagation();
    const url = `${getBackendUrl()}/fileSystemServer/getRateAnalysisAttachedFile/${params.fileKey}`;
    window.open(url, "_blank");
  };

  const headerContent = () => (
    <div className="  w-full flex justify-between rounded-md items-center">
      <div className="">{viewMode ? t("View Attachments") : t("Add Attachments")}</div>
    </div>
  );

  const toolbarClickHandler = useCallback((params: string) => {
    const toolClicked = params;
    const selectedRecords =
      dataGrid.current?.slickGrid
        .getSelectedRows()
        .map((data: number) => dataGrid.current?.slickGrid.getDataItem(data))
        .filter((item): item is Attachment => Boolean(item)) ?? [];
    const selectedRecordsKeys = selectedRecords.map((file: Attachment) => file.name);
    if (toolClicked == "delete_attachment" && selectedRecords.length > 0) {
      setAttachedFilesData((prev) => prev.filter((file: Attachment) => !selectedRecordsKeys.includes(file.name)));
      dataGrid.current?.refresh();
    }
  }, []);

  const handleRowSelection = () => {
    if (dataGrid.current && !viewMode) {
      const selectedRecords = dataGrid.current.slickGrid.getSelectedRows();
      if (selectedRecords.length == 0) {
        setRemoveBtnDisabled(true);
      } else {
        setRemoveBtnDisabled(false);
      }
    }
  };

  function fileNameTemplate(_data: unknown, _value: unknown, _a: unknown, _b: unknown, params: Attachment) {
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(
      <div className="flex gap-1 items-center">
        {getFileIcon(params.name)}
        <span>{params.name}</span>
      </div>,
    );
    return container;
  }

  function downLoadTemplate(_data: unknown, _value: unknown, _a: unknown, _b: unknown, params: Attachment) {
    const container = document.createElement("div");
    const root = createRoot(container);

    if (params.fileKey) {
      root.render(
        <IconComponent
          name="download"
          width="1.5rem"
          height="1.5rem"
          className="cursor-pointer"
          onClick={handleDownloadClick(params)}
        />,
      );
    }
    return container;
  }

  function sizeTemplate(_data: unknown, _value: unknown, _a: unknown, _b: unknown, params: Attachment) {
    return formatBytes(params.size);
  }

  function dateTemplate(_data: unknown, _value: unknown, _a: unknown, _b: unknown, params: Attachment) {
    const container = document.createElement("div");
    const root = createRoot(container);
    const dateFormat = convertDate(Number(params.modifiedDate), "dateForTooltip");
    root.render(
      <div style={{ width: "fit-content" }}>
        <ToolTipComponent showTipPointer position="bottom" content={dateFormat}>
          <span>{convertDate(Number(params.modifiedDate), "dateForText")}</span>
        </ToolTipComponent>
      </div>,
    );
    return container;
  }

  const attachFilesClickHandler = () => {
    handleAttachFiles(attachedFilesData);
  };

  const attachedNewFilesGrid = () => {
    return (
      <SlickGrid1
        ref={dataGrid as any}
        disableCheckBoxSelection={viewMode}
        enableSortingOption
        columnDefinitions={rateAnalysisAttachementsDefinition.current as any}
        dataset={attachedFilesData}
        toolbar={(!viewMode && toolbarOptions) as any}
        toolbarClickHandler={toolbarClickHandler}
        onSelectedRowIdsChanged={handleRowSelection}
        infiniteScroll
        enableFilteringOption
        enableGroupingOption
        enableColumnChooser
        excelExport
        hideNoDataIllustration
      />
    );
  };

  const modelContent = () => (
    <div className="flex gap-4 flex-col max-h-115 overflow-auto">
      {!viewMode && (
        <div className="flex h-16 border-2 border-button_light rounded-md justify-center items-center">
          <input
            type="file"
            name=""
            value=""
            accept="image/png,image/jpg,image/jpeg,application/pdf"
            id="image"
            onChange={handleChange}
          />
        </div>
      )}
      {attachedFilesData.length || attachments.length ? (
        <div className="flex flex-col border-2 border-button_light rounded-t-md h-90">{attachedNewFilesGrid()}</div>
      ) : null}
    </div>
  );

  const footerContent = () =>
    (attachedFilesData.length || attachments.length) && !viewMode ? (
      <div className="flex h-14 items-center gap-6 ">
        <ButtonComponent onClick={attachFilesClickHandler} buttonType={BUTTON_TYPES.GRADIENT_BUTTON}>
          {t("Attach Selected")}
        </ButtonComponent>
        <ButtonComponent onClick={closeAttachmentModal} buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}>
          {t("Cancel")}
        </ButtonComponent>
      </div>
    ) : undefined;

  return (
    <ModalStateDialogComponent
      allowModalPadding={false}
      width="70%"
      headerTitle={headerContent()}
      onCancel={closeAttachmentModal}
      modelContent={modelContent()}
      footerContent={footerContent()}
      isVisible
    />
  );
}

export default Attachments;
